<script setup>
import { ref, onMounted } from 'vue'
import { useUserStore } from '../../stores/userStore'
import TimezoneSelector from '../TimezoneSelector.vue'
import BoxscoresManagementSettings from '../../views/settings/BoxscoresManagementSettings.vue'
import axios from '../../axios.js'

const props = defineProps({
  activeTab: {
    type: String,
    required: true
  }
})

const userStore = useUserStore()

// General Settings
const generalSettings = ref({
  siteName: 'OddsComposer',
  timezone: 'UTC',
  dateFormat: 'DD.MM.YYYY',
  timeFormat: '24h',
  defaultLanguage: 'en',
  maintenanceMode: false
})

// Appearance Settings
const appearanceSettings = ref({
  theme: 'light',
  fontSize: 'medium',
  compactMode: false
})

// Notification Settings
const notificationSettings = ref({
  emailNotifications: true,
  pushNotifications: false,
  notifyOnGameStart: true,
  notifyOnStatusChange: true,
  dailyReport: true,
  weeklyReport: true
})

// Security Settings
const securitySettings = ref({
  twoFactorAuth: false,
  sessionTimeout: 30,
  passwordExpiry: 90,
  ipWhitelist: '',
  maxLoginAttempts: 5,
  requireStrongPassword: true
})

// Integration Settings
const integrationSettings = ref({
  apiKey: 'ak_live_****1234',
  webhookUrl: '',
  callbackUrl: '',
  apiRateLimit: 1000,
  enableWebhooks: false,
  enableCallbacks: false
})

// Timezone management
const savingTimezone = ref(false)
const showTimezoneSuccessPopup = ref(false)
const updatedTimezone = ref('')

// Load user timezone
const loadUserTimezone = async () => {
  try {
    const userId = userStore.currentUser?.id || JSON.parse(localStorage.getItem('currentUser') || '{}').id
    if (!userId) return
    
    if (userStore.currentUser?.timezone) {
      generalSettings.value.timezone = userStore.currentUser.timezone
    } else {
      const response = await axios.get(`/api/users/${userId}`)
      if (response.data?.timezone) {
        generalSettings.value.timezone = response.data.timezone
      }
    }
  } catch (error) {
    console.error('Failed to load timezone:', error)
  }
}

// Save timezone
const saveTimezone = async () => {
  savingTimezone.value = true
  try {
    const userId = userStore.currentUser?.id || JSON.parse(localStorage.getItem('currentUser') || '{}').id
    if (!userId) return
    
    const response = await userStore.updateUser(userId, { timezone: generalSettings.value.timezone })
    
    if (response) {
      if (userStore.currentUser) {
        userStore.currentUser.timezone = generalSettings.value.timezone
      }
      
      updatedTimezone.value = generalSettings.value.timezone
      showTimezoneSuccessPopup.value = true
      
      setTimeout(() => {
        showTimezoneSuccessPopup.value = false
      }, 3000)
    }
  } catch (error) {
    console.error('Failed to save timezone:', error)
    alert('Failed to save timezone. Please try again.')
  } finally {
    savingTimezone.value = false
  }
}

onMounted(() => {
  loadUserTimezone()
})
</script>

<template>
  <div class="space-y-6">
    <!-- General Settings -->
    <div v-if="activeTab === 'general'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">General</h2>
        <p class="text-gray-600">Configure basic application settings</p>
      </div>

      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
            <input 
              v-model="generalSettings.siteName"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <TimezoneSelector 
              :selected-timezone="generalSettings.timezone"
              @update:selected-timezone="(timezone) => generalSettings.timezone = timezone"
            />
            <button 
              @click="saveTimezone"
              :disabled="savingTimezone"
              class="mt-3 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <i v-if="savingTimezone" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-save"></i>
              {{ savingTimezone ? 'Saving...' : 'Save Timezone' }}
            </button>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
            <select 
              v-model="generalSettings.dateFormat"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="DD.MM.YYYY">DD.MM.YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Time Format</label>
            <select 
              v-model="generalSettings.timeFormat"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="24h">24-hour</option>
              <option value="12h">12-hour</option>
            </select>
          </div>
        </div>

        <div class="pt-6 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="font-medium text-gray-900">Maintenance Mode</h4>
              <p class="text-sm text-gray-500">Enable maintenance mode to prevent access to the platform</p>
            </div>
            <div class="relative inline-block w-12 h-6">
              <input 
                type="checkbox" 
                v-model="generalSettings.maintenanceMode"
                class="sr-only peer"
              >
              <div 
                class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                       peer-checked:bg-pink-500 transition-colors"
              ></div>
              <div 
                class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                       peer-checked:translate-x-6"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Appearance Settings -->
    <div v-if="activeTab === 'appearance'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Appearance</h2>
        <p class="text-gray-600">Customize the look and feel of the application</p>
      </div>

      <div class="space-y-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Theme</label>
          <select 
            v-model="appearanceSettings.theme"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="auto">Auto</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Font Size</label>
          <select 
            v-model="appearanceSettings.fontSize"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>

        <div class="pt-6 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="font-medium text-gray-900">Compact Mode</h4>
              <p class="text-sm text-gray-500">Reduce spacing for a more compact interface</p>
            </div>
            <div class="relative inline-block w-12 h-6">
              <input 
                type="checkbox" 
                v-model="appearanceSettings.compactMode"
                class="sr-only peer"
              >
              <div 
                class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                       peer-checked:bg-pink-500 transition-colors"
              ></div>
              <div 
                class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                       peer-checked:translate-x-6"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Settings -->
    <div v-if="activeTab === 'notifications'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Notifications</h2>
        <p class="text-gray-600">Manage your notification preferences</p>
      </div>

      <div class="space-y-6">
        <div v-for="(value, key) in notificationSettings" :key="key" class="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
          <div>
            <h4 class="font-medium text-gray-900">{{ key.replace(/([A-Z])/g, ' $1').trim() }}</h4>
            <p class="text-sm text-gray-500">Receive notifications for {{ key.toLowerCase() }}</p>
          </div>
          <div class="relative inline-block w-12 h-6">
            <input 
              type="checkbox" 
              v-model="notificationSettings[key]"
              class="sr-only peer"
            >
            <div 
              class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                     peer-checked:bg-pink-500 transition-colors"
            ></div>
            <div 
              class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                     peer-checked:translate-x-6"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Security Settings -->
    <div v-if="activeTab === 'security'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Security</h2>
        <p class="text-gray-600">Configure security and privacy settings</p>
      </div>

      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
            <input 
              v-model="securitySettings.sessionTimeout"
              type="number"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Password Expiry (days)</label>
            <input 
              v-model="securitySettings.passwordExpiry"
              type="number"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Team Settings -->
    <div v-if="activeTab === 'team'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Team</h2>
        <p class="text-gray-600">Manage your organization team members</p>
      </div>

      <div class="text-center py-12 text-gray-500">
        <i class="fas fa-users text-4xl mb-4"></i>
        <p>Team management features coming soon</p>
      </div>
    </div>

    <!-- Integration Settings -->
    <div v-if="activeTab === 'integrations'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Integrations</h2>
        <p class="text-gray-600">Configure API and integration settings</p>
      </div>

      <div class="space-y-6">
        <div class="grid grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">API Key</label>
            <div class="flex">
              <input 
                v-model="integrationSettings.apiKey"
                type="text"
                readonly
                class="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg bg-gray-50"
              >
              <button class="px-4 py-2 bg-gray-100 border-t border-r border-b border-gray-300 rounded-r-lg hover:bg-gray-200">
                <i class="far fa-copy"></i>
              </button>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">API Rate Limit</label>
            <input 
              v-model="integrationSettings.apiRateLimit"
              type="number"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Boxscores Management -->
    <div v-if="activeTab === 'boxscores-management'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Boxscores Management</h2>
        <p class="text-gray-600">Manage boxscores data and settings</p>
      </div>

      <BoxscoresManagementSettings />
    </div>
  </div>
</template>

<style scoped>
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}
</style>
